import { ref } from 'vue';
import Http from '@/services/Http';
import { usePermissions } from './usePermissions';

// Interface for column settings
export interface ColumnSettings {
    name: string;
    visible: boolean;
    width?: number;
    locked?: boolean;
    order: number;
}

// Interface for formatting options (always loaded, not URL-driven)
export interface FormattingOptions {
    columns: ColumnSettings[];
    pageSize?: number;
    sort?: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Interface for filter options (URL-driven, persisted separately)
export interface FilterOptions {
    [key: string]: any;
}

// Interface for complete datatable settings
export interface DatatableSettings {
    filters: FilterOptions;
    formatting: FormattingOptions;
}

// Cache for settings to avoid repeated API calls
const settingsCache = new Map<string, DatatableSettings>();

/**
 * Helper to check if a filter object has any non-empty values
 */
export function hasNonEmptyFilters(filters: FilterOptions | null | undefined): boolean {
    if (!filters) return false;
    
    for (const key in filters) {
        const value = filters[key];
        if (value === null || value === undefined) continue;
        if (Array.isArray(value) && value.length > 0) return true;
        if (typeof value === 'string' && value.trim() !== '') return true;
        if (typeof value === 'boolean' && value === true) return true;
        if (typeof value === 'number') return true;
    }
    return false;
}

/**
 * Composable for managing datatable customization settings.
 * Settings are persisted to the TenantOption table and cached locally.
 * 
 * @param datatableKey - Unique identifier for the datatable (e.g., 'RequestsDatatable')
 */
export function useDatatableSettings(datatableKey: string) {
    const { userId } = usePermissions();
    const isLoading = ref(false);
    const settings = ref<DatatableSettings | null>(null);
    const hasLoaded = ref(false);

    // Generate the option key for this datatable and user
    const getOptionKey = () => `datatable_settings_${datatableKey}`;

    /**
     * Load settings from cache or API
     */
    const loadSettings = async (): Promise<DatatableSettings | null> => {
        if (!userId.value) {
            console.debug('useDatatableSettings: No user ID available');
            return null;
        }

        const cacheKey = `${datatableKey}_${userId.value}`;
        
        // Check cache first
        if (settingsCache.has(cacheKey)) {
            settings.value = settingsCache.get(cacheKey) || null;
            hasLoaded.value = true;
            return settings.value;
        }

        isLoading.value = true;
        try {
            const optionKey = getOptionKey();
            const response = await Http.get(`/options/${optionKey}`, {
                refKey: 'Technician',
                refVal: userId.value.toString()
            });

            if (response?.OptionValue) {
                const parsed = JSON.parse(response.OptionValue) as DatatableSettings;
                settings.value = parsed;
                settingsCache.set(cacheKey, parsed);
            } else {
                settings.value = null;
            }
            hasLoaded.value = true;
            return settings.value;
        } catch (error) {
            console.error('Error loading datatable settings:', error);
            hasLoaded.value = true;
            return null;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Save settings to API and update cache
     */
    const saveSettings = async (newSettings: DatatableSettings): Promise<boolean> => {
        if (!userId.value) {
            console.debug('useDatatableSettings: No user ID available for saving');
            return false;
        }

        const cacheKey = `${datatableKey}_${userId.value}`;
        
        try {
            const optionKey = getOptionKey();
            await Http.bgPost('/options', {
                OptionKey: optionKey,
                OptionValue: JSON.stringify(newSettings),
                RefKey: 'Technician',
                RefVal: userId.value.toString()
            });

            // Update local state and cache
            settings.value = newSettings;
            settingsCache.set(cacheKey, newSettings);
            return true;
        } catch (error) {
            console.error('Error saving datatable settings:', error);
            return false;
        }
    };

    /**
     * Get default empty settings structure
     */
    const getDefaultSettings = (): DatatableSettings => ({
        filters: {},
        formatting: { columns: [] }
    });

    /**
     * Update only the filters portion of settings
     */
    const saveFilters = async (filters: FilterOptions): Promise<boolean> => {
        const currentSettings = settings.value || getDefaultSettings();
        return saveSettings({
            ...currentSettings,
            filters
        });
    };

    /**
     * Update only the formatting portion of settings (columns, pageSize, sort)
     */
    const saveFormatting = async (formatting: Partial<FormattingOptions>): Promise<boolean> => {
        const currentSettings = settings.value || getDefaultSettings();
        return saveSettings({
            ...currentSettings,
            formatting: {
                ...currentSettings.formatting,
                ...formatting
            }
        });
    };

    /**
     * Update only the columns portion of formatting
     */
    const saveColumns = async (columns: ColumnSettings[]): Promise<boolean> => {
        return saveFormatting({ columns });
    };

    /**
     * Update page size in formatting
     */
    const savePageSize = async (pageSize: number): Promise<boolean> => {
        return saveFormatting({ pageSize });
    };

    /**
     * Update sort settings in formatting
     */
    const saveSort = async (sort: Array<Record<string, 'ASC' | 'DESC'>>): Promise<boolean> => {
        return saveFormatting({ sort });
    };

    /**
     * Clear settings for this datatable
     */
    const clearSettings = async (): Promise<boolean> => {
        if (!userId.value) return false;

        const cacheKey = `${datatableKey}_${userId.value}`;
        
        try {
            const optionKey = getOptionKey();
            await Http.bgPost('/options/delete', {
                OptionKey: optionKey,
                RefKey: 'Technician',
                RefVal: userId.value.toString()
            });

            settings.value = null;
            settingsCache.delete(cacheKey);
            return true;
        } catch (error) {
            console.error('Error clearing datatable settings:', error);
            return false;
        }
    };

    /**
     * Get filter value with fallback to default
     */
    const getFilter = <T>(key: string, defaultValue: T): T => {
        if (!settings.value?.filters) return defaultValue;
        return settings.value.filters[key] ?? defaultValue;
    };

    /**
     * Invalidate cache for this datatable (useful after logout/login)
     */
    const invalidateCache = () => {
        if (!userId.value) return;
        const cacheKey = `${datatableKey}_${userId.value}`;
        settingsCache.delete(cacheKey);
        hasLoaded.value = false;
    };

    return {
        settings,
        isLoading,
        hasLoaded,
        loadSettings,
        saveSettings,
        saveFilters,
        saveFormatting,
        saveColumns,
        savePageSize,
        saveSort,
        clearSettings,
        getFilter,
        getDefaultSettings,
        invalidateCache
    };
}

export default useDatatableSettings;